import { auth } from '../config/firebase';
import {
  CreateInvitationRequest,
  InvitationResponse,
  PublicInvitationResponse,
  AcceptInvitationResponse
} from '../types/invitation';
import { RateLimitError } from '../types';

const SHOP_API_BASE_URL = process.env.REACT_APP_SHOP_API_BASE_URL || 'https://shop.dev.carouselshopai.com';

class InvitationService {
  private async getAuthToken(): Promise<string | null> {
    const user = auth.currentUser;
    if (user) {
      return await user.getIdToken();
    }
    return null;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    requiresAuth: boolean = true
  ): Promise<T> {
    const token = requiresAuth ? await this.getAuthToken() : null;
    const url = `${SHOP_API_BASE_URL}${endpoint}`;

    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    };

    const response = await fetch(url, config);

    if (!response.ok) {
      // Handle rate limit errors specially
      if (response.status === 429) {
        const errorData = await response.json();
        throw new RateLimitError({
          resourceType: errorData.resourceType || 'ACTIVE_MEMBERS',
          currentUsage: errorData.currentUsage || 0,
          maxAllowed: errorData.maxAllowed || 0,
          tierType: errorData.tierType || 'UNKNOWN',
          message: errorData.message || errorData.rateLimitMessage || 'Rate limit exceeded'
        });
      }
      // CSAI-533: surface the server's own message rather than burying it in "HTTP 400: {json}".
      //
      // Not every refusal arrives as a 429. The invitations-per-day cap throws
      // IllegalArgumentException in shop-svc, which the controller maps to 400 with
      // {"error": "..."} - so a PERSONAL_GUEST shop (0 invitations per day) gets a refusal the
      // client used to replace with "check the email and try again", blaming the email for a plan
      // limit. The message is already accurate; it was simply being discarded.
      const errorText = await response.text();
      let serverMessage: string | null = null;
      try {
        const parsed = JSON.parse(errorText);
        serverMessage = parsed?.error || parsed?.message || null;
      } catch {
        // Not JSON. Fall through to the status-code form below.
      }
      throw new Error(serverMessage || `HTTP ${response.status}: ${errorText || response.statusText}`);
    }

    if (response.status === 204) {
      return {} as T;
    }

    return response.json();
  }

  /**
   * Create an invitation for a new shop member
   */
  async createInvitation(shopId: string, request: CreateInvitationRequest): Promise<InvitationResponse> {
    return this.request<InvitationResponse>(`/api/v1/shops/${shopId}/invitations`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  /**
   * List all pending invitations for a shop
   */
  async listPendingInvitations(shopId: string): Promise<InvitationResponse[]> {
    return this.request<InvitationResponse[]>(`/api/v1/shops/${shopId}/invitations?status=PENDING`);
  }

  /**
   * Revoke an invitation
   */
  async revokeInvitation(shopId: string, invitationId: string): Promise<void> {
    await this.request<void>(`/api/v1/shops/${shopId}/invitations/${invitationId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Get invitation details by token (public endpoint - no auth required)
   */
  async getInvitationByToken(token: string): Promise<PublicInvitationResponse> {
    return this.request<PublicInvitationResponse>(`/api/v1/invitations/${token}`, {}, false);
  }

  /**
   * Accept an invitation (requires authentication)
   */
  async acceptInvitation(token: string): Promise<AcceptInvitationResponse> {
    return this.request<AcceptInvitationResponse>(`/api/v1/invitations/${token}/accept`, {
      method: 'POST',
    });
  }
}

export const invitationService = new InvitationService();
